import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ThemeProvider, CssBaseline, Box, Toolbar, Typography, TextField, Button, Divider, Tabs, Tab, Alert } from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from 'react-select';
import { countries } from 'countries-list';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import theme from '../Theme';
import { API_URL } from '../apiConfig';

const countryOptions = Object.entries(countries)
  .map(([code, country]) => ({ value: code, label: country.name, code: country.phone, flag: code.toLowerCase() }))
  .sort((a, b) => a.label.localeCompare(b.label));

const Settings = ({ darkMode, setDarkMode, handleLogout }) => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [country, setCountry] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/get-csrf-token`, { withCredentials: true });
        setCsrfToken(data.csrf_token);
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    const fetchData = async () => {
      const token = localStorage.getItem('accessToken');
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/user_details`, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data');
        setLoading(false);
      }
    };

    fetchCsrfToken();
    fetchData();
  }, []);

  const handleProfileChange = (e) => setProfile({ ...profile, [e.target.name]: e.target.value });

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
    setProfile({ ...profile, phone_number: `+${selectedOption.code}` });
  };

  const handlePhoneChange = (phone, country) => {
    const formattedPhone = `+${country.dialCode} ${phone.slice(country.dialCode.length)}`;
    setProfile({ ...profile, phone_number: formattedPhone });
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('accessToken');
    try {
      await axios.put(`${API_URL}/api/user_details`, { phone_number: profile.phone_number }, {
        headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
        withCredentials: true
      });
      alert('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  const handlePasswordResetRequest = async () => {
    const token = localStorage.getItem('accessToken');
    const endpoint = profile.type === 'user' ? `${API_URL}/api/request_password_reset` : `${API_URL}/api/request_team_member_password_reset`;
    try {
      const { data } = await axios.post(endpoint, { email: profile.email }, {
        headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
        withCredentials: true
      });
      setResetPasswordMessage(data.message);
      setResetPasswordError('');
    } catch (error) {
      const errorMessage = error.response ? error.response.data.message : 'An error occurred';
      setResetPasswordError(errorMessage);
      setResetPasswordMessage('');
    }
  };
  

  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  const handleResendVerificationCode = async () => {
    const token = localStorage.getItem('accessToken');
    setCanResend(false);
    try {
      const { data } = await axios.post(`${API_URL}/api/resend_verification_code`, { email: profile.email }, {
        headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
        withCredentials: true
      });
      setResendMessage(data.message);
      setShowVerificationInput(true);
      setTimeout(() => setCanResend(true), 60000);
    } catch (error) {
      console.error('Error resending verification code:', error);
      setResendMessage('Failed to resend verification code');
      setCanResend(true);
    }
  };

  const handleVerifyEmail = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      const { data, status } = await axios.post(`${API_URL}/api/verify_email`, {
        email: profile.email,
        verification_code: verificationCode
      }, {
        headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
        withCredentials: true
      });
      setVerificationMessage(data.message);
      if (status === 200) {
        setProfile({ ...profile, email_verified: true });
        setShowVerificationInput(false);
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      setVerificationMessage('Failed to verify email');
    }
  };

  if (loading || error) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <TopBar darkMode={darkMode} setDarkMode={setDarkMode} handleLogout={handleLogout} />
          <Sidebar handleLogout={handleLogout} />
          <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
            <Toolbar />
            <Typography variant="h6">{loading ? 'Loading...' : <Typography color="error">{error}</Typography>}</Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <TopBar darkMode={darkMode} setDarkMode={setDarkMode} handleLogout={handleLogout} />
        <Sidebar handleLogout={handleLogout} />
        <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3 }}>
          <Toolbar />
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label="General Profile" />
            <Tab label="Security & Privacy" />
            <Tab label="Notifications" />
          </Tabs>
          <Divider sx={{ my: 2 }} />

          {tabIndex === 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6">General Profile</Typography>
              <form onSubmit={handleProfileUpdate} sx={{ mt: 1 }}>
                <TextField label="Email" value={profile.email || ''} fullWidth sx={{ mb: 2 }} disabled />
                {/* Hide resend email verification for team members */}
                {!profile.email_verified && profile.type !== 'team_member' && (
                  <Box sx={{ mb: 2 }}>
                    <Button variant="contained" onClick={handleResendVerificationCode} disabled={!canResend}>Resend Verification Code</Button>
                    <Alert severity="warning" sx={{ mb: 2 }}>Your email is not verified. Please verify your email to continue.</Alert>
                    {resendMessage && <Alert severity="warning" sx={{ mt: 2 }}>{resendMessage}</Alert>}
                    {showVerificationInput && (
                      <Box sx={{ mt: 2 }}>
                        <TextField label="Verification Code" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} fullWidth sx={{ mb: 1 }} />
                        <Button variant="contained" onClick={handleVerifyEmail}>Verify Email</Button>
                        {verificationMessage && <Alert severity="warning" sx={{ mt: 2 }}>{verificationMessage}</Alert>}
                      </Box>
                    )}
                  </Box>
                )}
                <PhoneInput country={country ? country.value.toLowerCase() : ''} value={profile.phone_number} onChange={handlePhoneChange} specialLabel="Phone Number" />
                <Button variant="contained" type="submit" sx={{ mt: 2 }}>Update Profile</Button>
              </form>
            </Box>
          )}

          {tabIndex === 1 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6">Security & Privacy</Typography>
              <Button variant="contained" onClick={handlePasswordResetRequest}>Request Password Reset</Button>
              {resetPasswordMessage && <Alert severity="success" sx={{ mt: 2 }}>{resetPasswordMessage}</Alert>}
              {resetPasswordError && <Alert severity="error" sx={{ mt: 2 }}>{resetPasswordError}</Alert>}
            </Box>
          )}

          {tabIndex === 2 && <Box sx={{ mb: 4 }}><Typography variant="h6">Notifications</Typography></Box>}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Settings;
